<template>
    <InputMulti
        :value="props.value"
        :options="props.options"
        value-key="value"
        label-key="label"
        :label="props.label"
        :name="props.name"
        :rules="props.rules"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :api-validation-error="props.apiValidationError"
        :multiple="false"
        @input="val => emit('input', val)"
    />
</template>

<script setup>
    import InputMulti from '@/modules/front/ui/InputMulti.vue';

    /**
     * DocumentTypePicker — generic document type selector primitive.
     *
     * Wraps InputMulti (vue-multiselect) for single selection, preconfigured for
     * document-type option shapes ({ value, label }).
     *
     * API change vs original:
     *   - @change removed — InputMulti only emits `input`. Use @input or v-model.
     *   - `searchable` built-in from InputMulti (can be suppressed via $attrs if needed).
     *
     * @prop {String}  value               - Currently selected value (v-model compatible via @input).
     * @prop {Array}   options             - List of { value: string, label: string } objects.
     * @prop {String}  [label]             - Floating label text.
     * @prop {String}  [name]              - Field name for VeeValidate.
     * @prop {String|Object} [rules]       - VeeValidate rules.
     * @prop {String}  [placeholder]       - Placeholder shown when no value is selected.
     * @prop {Boolean} [disabled]          - Disables the picker.
     * @prop {String|Array} [apiValidationError] - Server-side validation error to display.
     *
     * @emits input  - New selected value (string).
     */
    const props = defineProps({
        value: String,
        options: {
            type: Array,
            default: () => [],
            validator: opts => opts.every(o => 'value' in o && 'label' in o),
        },
        label: String,
        name: String,
        rules: [String, Object],
        placeholder: String,
        disabled: Boolean,
        apiValidationError: [String, Array],
    });

    const emit = defineEmits(['input']);
</script>

<script>
    export default {
        inheritAttrs: false
    };
</script>
